import React, { useState } from "react";
import { ShieldCheck, Landmark, Smartphone, Copy, Check, Heart } from "lucide-react";
import { Container, Reveal, Overline } from "./common";
import { CONTACT } from "../lib/content";

const PRESETS = [500, 1000, 2500, 5000, 10000];

const BANK = {
  "Account Name": "Janma Foundation",
  "Bank": "HDFC Bank",
  "A/C No.": "50200086344505",
  "IFSC": "HDFC0005034",
  "Branch": "Isnapur (Patancheru), Hyderabad",
  "Account Type": "Current Account",
};

const Row = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-teal-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="flex items-center gap-2 font-medium text-navy">
        <span className="font-mono text-sm">{value}</span>
        <button
          onClick={copy}
          aria-label={`Copy ${label}`}
          data-testid={`copy-${label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
          className="text-teal-600 hover:text-coral transition-colors"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </span>
    </div>
  );
};

export const DonateSection = () => {
  const [amount, setAmount] = useState(1000);
  return (
    <section id="donate" className="scroll-mt-24 py-20 lg:py-28 bg-teal-900 relative overflow-hidden" data-testid="donate-section">
      <div className="absolute inset-0 grain opacity-[0.07] pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-teal-700/40 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 h-80 w-80 rounded-full bg-coral/20 blur-3xl" />
      <Container className="relative">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <Overline light>Make a Direct Impact</Overline>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 leading-[1.08] text-balance">
              Your gift reaches a child, a mother, an elder — directly.
            </h2>
            <p className="mt-5 text-teal-100/90 text-lg leading-relaxed">
              100% of your contribution funds education, healthcare, skills and shelter on the
              ground in Telangana & Andhra Pradesh, and increasingly across India. Every donation
              is eligible for tax exemption.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white">
              <ShieldCheck className="h-4.5 w-4.5 text-coral" />
              Tax Exempt under Section 80G
            </div>

            <div className="mt-8">
              <p className="text-teal-200 text-sm mb-3 font-mono uppercase tracking-wider">Choose an amount</p>
              <div className="flex flex-wrap gap-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setAmount(p)}
                    data-testid={`donate-amount-${p}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-all ${
                      amount === p
                        ? "bg-coral border-coral text-white shadow-soft"
                        : "bg-white/5 border-white/20 text-white hover:border-coral"
                    }`}
                  >
                    ₹{p.toLocaleString("en-IN")}
                  </button>
                ))}
              </div>
              <a
                href={`mailto:${CONTACT.email}?subject=Donation of ₹${amount} to Janma Foundation&body=I would like to donate ₹${amount}. Please share the payment and 80G receipt details.`}
                data-testid="donate-proceed-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-7 py-4 text-base font-semibold text-white hover:bg-coral-700 transition-all hover:-translate-y-0.5"
              >
                <Heart className="h-5 w-5 fill-white" /> Donate ₹{amount.toLocaleString("en-IN")}
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white p-7 sm:p-9 shadow-glow">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-2xl bg-teal-50 text-teal-700">
                  <Landmark className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-lg text-navy">Bank Transfer (NEFT / IMPS)</h3>
                  <p className="text-sm text-slate-500">Direct to the foundation account</p>
                </div>
              </div>
              <div className="mt-5">
                {Object.entries(BANK).map(([k, v]) => (
                  <Row key={k} label={k} value={v} />
                ))}
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-2xl bg-teal-50 p-4 text-sm text-teal-900">
                <Smartphone className="h-5 w-5 shrink-0 text-teal-600 mt-0.5" />
                <p>
                  Please confirm the latest account details and request your 80G tax receipt by writing to{" "}
                  <a href={`mailto:${CONTACT.email}`} className="font-semibold underline">
                    {CONTACT.email}
                  </a>
                  .
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};
